/**
 * Constants for RAO — the 6-stage ribbon (officer-facing) and 9-step pipeline
 * (backend) are kept separate so the two vocabularies don't bleed.
 *
 * - The 6 stages drive the stage-ribbon across the top of CartDetail.
 * - The 9 steps drive the PipelineProgress tracker (commit surfaces; the
 *   live commit flow itself is CommitFlow.tsx — CommitStep was retired
 *   S1.1 2026-06-11).
 */

import type { StageRibbonStep } from '@/components/primitives';

export const RAO_STAGE_IDS = [
  'people',
  'products',
  'ownership',
  'compliance',
  'review',
  'sign',
  'commit',
] as const;

export type RaoStageId = (typeof RAO_STAGE_IDS)[number];

export interface RaoStageDef extends StageRibbonStep {
  id: RaoStageId;
}

/**
 * Officer-facing stage ribbon.
 *
 * Wave 1 #2.8 (Bill 2026-04-28) — collapsed from 7 stages to SPFx canonical
 * 3 (People / Products / Review & Submit). Ownership / CIP-OFAC / Signing /
 * Commit are no longer top-level stages:
 *   - Per-product config (ownership chips, services, POD beneficiaries) lives
 *     inside the AddToCart product modal on the Products step (BACKLOG-0721:
 *     editable cart-item stack; the standalone OwnershipStep/ServiceEnrollmentMatrix
 *     were deleted, and POD Beneficiaries are captured per-product in AddToCartModal)
 *   - Beneficial Ownership (FinCEN CDD, business accounts) is entity-scoped and
 *     lives in the business-entity person dialog, NOT with Products (Bill 2026-06-01)
 *   - CIP / OFAC capture happens on the per-person dialog within People
 *   - Signing + Commit run from the Review & Submit stage when the officer
 *     hits the resolve action — they're pipeline phases, not officer stages
 *
 * The 'ownership' / 'compliance' / 'sign' / 'commit' ids stay reachable as
 * legacy aliases by routing logic so URLs / events / cart.currentStage rows
 * created prior to v0.1.84 don't 404 — they collapse onto 'review'.
 */
export const RAO_STAGES: RaoStageDef[] = [
  { id: 'people',   label: 'People' },
  { id: 'products', label: 'Products' },
  { id: 'review',   label: 'Review & Submit' },
];

/**
 * Legacy stage-id aliases retained for back-compat. Any cart row stamped with
 * one of these `currentStage` values is treated as the value on the right.
 */
export const RAO_STAGE_ALIASES: ReadonlyMap<string, string> = new Map<string, string>([
  ['ownership',  'products'],
  ['compliance', 'people'],
  ['sign',       'review'],
  ['commit',     'review'],
]);

/**
 * 9-step backend pipeline ("CreateCifs" → "CreateRelationships") — used by
 * PipelineProgress to mirror PipelineStepExecutions rows.  Step codes match
 * the `stepCode` shape in `PipelineSnapshot` from shared-types.
 */
export interface RaoPipelineStepDef {
  stepCode: string;
  label: string;
  description: string;
}

export const RAO_PIPELINE_STEPS: RaoPipelineStepDef[] = [
  { stepCode: 'CreateCifs',             label: 'Create CIFs',             description: 'CustSrch preflight + CustAdd' },
  { stepCode: 'ProvisionAccountIds',    label: 'Reserve account IDs',     description: 'jXRL AcctIdGen' },
  { stepCode: 'SaveCipDocs',            label: 'Save CIP docs',           description: 'Persist ID captures to SharePoint' },
  { stepCode: 'ProduceDocs',            label: 'Produce documents',       description: 'Render disclosure PDFs with QR markers' },
  { stepCode: 'AwaitSignatures',        label: 'Await signatures',        description: 'Signing session started · cart paused until signed' },
  { stepCode: 'CreateAccounts',         label: 'Create accounts',         description: 'jXRL AcctAdd per product' },
  { stepCode: 'CreateCards',            label: 'Create debit cards',      description: 'jXRL EFTCardAdd (eligible products only)' },
  { stepCode: 'EnrollNetTeller',        label: 'Enroll in NetTeller',     description: 'OnlineBanking/IntnetFinInstIdAdd' },
  { stepCode: 'AddBeneficiariesRels',   label: 'Beneficiaries & relations', description: 'CustRelAdd · TaxPlnBenfAdd for IRAs' },
];

/**
 * Debit-card-eligibility matrix (modules digest §RAO gotchas).
 * Card is issued UNLESS the product is in this deny-list.
 */
export const DEBIT_CARD_INELIGIBLE_ENTITY_TYPES: ReadonlyArray<string> = [
  'Conservatorship',
  'Estate',
  'VA_Fiduciary',
  'USDA_Grant',
  'IRA',
  'CD',
  'SDB',
];

/**
 * Products discontinued in v3 — hide from selection even if SQL returns them.
 *
 * Code MUST match `Products.Code` from `scripts/seed/23-sp-products.sql`
 * (the SP-derived seed). Variable Rate IRA = `VARIRA` (not `IRA_VARIABLE` —
 * that legacy guess never matched any seeded row, so the discontinued check
 * was effectively dead code prior to 2026-04-24).
 */
export const DISCONTINUED_PRODUCT_CODES: ReadonlyArray<string> = [
  'VARIRA',
];
